import React, { useState, useEffect } from "react"
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Checkbox,
  Typography,
  CircularProgress,
  Box,
} from "@mui/material"
import { api, showWarning } from "../../../../api"

const ModalSeriesEgreso = ({ open, onClose, onSave, articulo, bodcodigo }) => {
  const [seriesDisponibles, setSeriesDisponibles] = useState([])
  const [selectedSeries, setSelectedSeries] = useState([])
  const [isLoading, setIsLoading] = useState(false)

  const isVehiculo = articulo?.tipserie === "V"
  const cantidadAAfectar = parseFloat(articulo?.cant_afectar) || 0

  // ==========================================================================
  // OBTENCIÓN DE SERIES DISPONIBLES Y RESTAURACIÓN DE ESTADO
  // ==========================================================================
  useEffect(() => {
    const fetchSeries = async () => {
      if (!open || !articulo) return

      setIsLoading(true)
      try {
        const payload = {
          invcodigo: articulo.invcodigo,
          bodcodigo,
          artcodigo: articulo.artcodigo,
        }
        const response = await api.post("/IngEgrporSolicitud/getSeriesDisponibles", payload)

        let data = []
        if (response?.data?.data) data = response.data.data
        else if (response?.data) data = response.data

        setSeriesDisponibles(data)

        // Si ya había series seleccionadas previamente en la grilla principal, las restauramos
        if (articulo.series && articulo.series.length > 0) {
          setSelectedSeries(articulo.series)
        } else {
          setSelectedSeries([])
        }
      } catch (error) {
        console.error("Error al cargar las series disponibles:", error)
        showWarning("Ocurrió un error al intentar cargar las series disponibles.")
      } finally {
        setIsLoading(false)
      }
    }

    fetchSeries()
  }, [open, articulo, bodcodigo])

  // ==========================================================================
  // MANEJADOR DE SELECCIÓN (CHECKBOX)
  // ==========================================================================
  const handleToggle = (serieObj) => {
    const currentIndex = selectedSeries.findIndex((s) => s.sernumero === serieObj.sernumero)
    const newSelected = [...selectedSeries]

    if (currentIndex === -1) {
      // Regla de Negocio: No permitir seleccionar más de la cantidad requerida
      if (newSelected.length >= cantidadAAfectar) {
        return showWarning(`Solo debe seleccionar exactamente ${cantidadAAfectar} series.`)
      }
      newSelected.push(serieObj)
    } else {
      // Si ya estaba seleccionado, lo quitamos
      newSelected.splice(currentIndex, 1)
    }

    setSelectedSeries(newSelected)
  }

  // ==========================================================================
  // VALIDACIÓN ESTRICTA Y GUARDADO
  // ==========================================================================
  const handleSave = () => {
    if (selectedSeries.length !== cantidadAAfectar) {
      return showWarning(
        `Ha seleccionado ${selectedSeries.length} series, pero indicó que afectaría ${cantidadAAfectar}. Deben coincidir exactamente.`,
      )
    }

    // Enviamos las series seleccionadas al componente padre
    onSave(selectedSeries)
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth={isVehiculo ? "lg" : "sm"} fullWidth>
      <DialogTitle sx={{ backgroundColor: "#196C87", color: "white", py: 1.5 }}>
        Selección de Series para Egreso
      </DialogTitle>

      <DialogContent sx={{ mt: 2 }}>
        <Typography variant="subtitle2" gutterBottom>
          Artículo: <b>{articulo?.artcodigo}</b> - {articulo?.artdescri}
        </Typography>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
          <Typography variant="body2" color="textSecondary">
            Bodega: {bodcodigo}
          </Typography>
          <Typography
            variant="body2"
            color={selectedSeries.length === cantidadAAfectar ? "primary" : "error"}
            fontWeight="bold"
          >
            Series Seleccionadas: {selectedSeries.length} / {cantidadAAfectar}
          </Typography>
        </Box>

        {isLoading ? (
          <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
            <CircularProgress color="primary" />
          </Box>
        ) : (
          <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 400 }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell padding="checkbox">✓</TableCell>
                  <TableCell width={isVehiculo ? "25%" : "85%"}>Número de Serie</TableCell>
                  {isVehiculo && (
                    <>
                      <TableCell width="15%">Motor</TableCell>
                      <TableCell width="15%">RAMV</TableCell>
                      <TableCell width="10%">Año</TableCell>
                      <TableCell width="10%">País</TableCell>
                      <TableCell width="15%">Color</TableCell>
                    </>
                  )}
                </TableRow>
              </TableHead>
              <TableBody>
                {seriesDisponibles.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={isVehiculo ? 7 : 2} align="center" sx={{ py: 3, color: "text.secondary" }}>
                      No se encontraron series disponibles (Activas) en esta bodega.
                    </TableCell>
                  </TableRow>
                ) : (
                  seriesDisponibles.map((row, index) => {
                    const isSelected = selectedSeries.some((s) => s.sernumero === row.sernumero)

                    return (
                      <TableRow
                        key={index}
                        hover
                        onClick={() => handleToggle(row)}
                        role="checkbox"
                        aria-checked={isSelected}
                        selected={isSelected}
                        sx={{ cursor: "pointer" }}
                      >
                        <TableCell padding="checkbox">
                          <Checkbox checked={isSelected} color="primary" />
                        </TableCell>
                        <TableCell fontWeight="bold">{row.sernumero}</TableCell>
                        {isVehiculo && (
                          <>
                            <TableCell>{row.motor}</TableCell>
                            <TableCell>{row.ram}</TableCell>
                            <TableCell>{row.anio}</TableCell>
                            <TableCell>{row.pais}</TableCell>
                            <TableCell>{row.color}</TableCell>
                          </>
                        )}
                      </TableRow>
                    )
                  })
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </DialogContent>

      <DialogActions sx={{ p: 2, backgroundColor: "#f5f7fa" }}>
        <Button onClick={onClose} color="inherit" variant="outlined">
          Cancelar
        </Button>
        <Button
          onClick={handleSave}
          color="primary"
          variant="contained"
          disabled={isLoading || selectedSeries.length !== cantidadAAfectar}
          sx={{ backgroundColor: "#196C87" }}
        >
          Confirmar Selección
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default ModalSeriesEgreso
